import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Chapter, Reveal, EASE } from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function Waitlist() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then((res) => setCount(res.data.count))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist`, { email });
      setJoined(true);
      setCount(res.data.count);
      if (res.data.status === "exists") {
        toast.info("You're already on the list.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" className="relative overflow-hidden py-32 sm:py-44" data-testid="waitlist-section">
      <div className="bg-blueprint absolute inset-0" aria-hidden="true" />
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(circle at 50% 100%, rgba(212,175,55,0.1) 0%, rgba(9,9,9,0) 60%)" }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
        <div className="flex justify-center">
          <Chapter index="07" label="Early Access" testid="waitlist-chapter" />
        </div>

        <Reveal>
          <h2
            className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
            data-testid="waitlist-headline"
          >
            Be there when pitsora <span className="text-[#D4AF37]">goes live.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15} className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-[#9A9A9A] sm:text-lg">
          <p>
            We're building pitsora now. Join the early access list and follow the journey from
            prototype to production studio.
          </p>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-12 min-h-[76px]">
            <AnimatePresence mode="wait">
              {joined ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.94, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="inline-flex items-center gap-4 border border-[#D4AF37]/40 bg-[#111111] px-8 py-5"
                  data-testid="waitlist-success-message"
                >
                  <CheckCircle2 className="h-6 w-6 shrink-0 text-[#D4AF37]" strokeWidth={1.5} />
                  <span className="text-left font-display text-lg font-semibold text-[#F5F5F5] sm:text-xl">
                    You're on the list. We'll see you inside pitsora.
                  </span>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                  onSubmit={submit}
                  className="mx-auto flex max-w-xl flex-col gap-3 sm:flex-row"
                >
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full flex-1 border border-white/15 bg-[#111111] px-6 py-4 font-mono text-sm text-[#F5F5F5] placeholder-[#555555] outline-none transition-colors duration-300 focus:border-[#D4AF37]/60"
                    data-testid="waitlist-email-input"
                    aria-label="Email address"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="group flex items-center justify-center gap-2.5 whitespace-nowrap bg-[#D4AF37] px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#090909] transition-colors duration-300 hover:bg-[#E5A93C] disabled:opacity-60"
                    data-testid="waitlist-submit-button"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        Join the Waitlist
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>

        <Reveal delay={0.35}>
          <div className="mt-6 flex flex-col items-center gap-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#666666]">
              No spam. Just occasional updates about pitsora.
            </p>
            {count !== null && count > 0 && (
              <span
                className="flex items-center gap-2 border border-white/8 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#9A9A9A]"
                data-testid="waitlist-counter-badge"
              >
                <span className="h-1 w-1 rounded-full bg-[#D4AF37]" />
                {count} {count === 1 ? "storyteller" : "storytellers"} on the list
              </span>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
